import Button from '@components/shared/Button'
import CommonPool from 'abis/CommonPool.json'
import { Token, getV2Tokens } from 'config/tokens'
import { createCommonPoolClaim } from 'domain/synthetics/markets/createCommonPoolClaim'
import { useTokenBalances } from 'domain/synthetics/tokens'
import { BigNumber, ethers } from 'ethers'
import { formatTokenAmount, numberWithCommas } from 'rfx/lib/numbers'
import { usePendingTxns } from 'rfx/lib/usePendingTxns'
import useWallet from 'rfx/lib/wallets/useWallet'
import useBreakpoints from 'hooks/useBreakpoints'
import { usePaymaster } from 'hooks/usePaymaster'
import { isNull } from 'lodash'
import { useState } from 'react'
import useSWR from 'swr'
import { cn } from 'utils/classnames'
import VaultSectionHeading from './VaultSectionHeading'
import Tooltip from '@components/shared/Tooltip'
import { useCommonPoolAPR } from 'hooks/vault/useCommonPoolAPR'
import dayjs from 'dayjs'
import { formatTradeActionTimestampISO } from 'components/Synthetics/TradeHistoryRow/utils/shared'
import commonPoolStore from '@store/commonPoolStore'
import CardRow from 'components/CardRow/CardRow'

const getFormattedData = (
  data: number | null,
  symbol: string,
  decimals: number = 18,
) => {
  return `${
    isNull(data)
      ? '...'
      : numberWithCommas((data * Math.pow(10, -decimals)).toFixed(4))
  } ${symbol}`
}

const VaultExposure = () => {
  const { below } = useBreakpoints()
  const { chainId, signer, account } = useWallet()
  const [, setPendingTxns] = usePendingTxns()
  const { sendPaymasterTransaction } = usePaymaster()
  const { selectedVault } = commonPoolStore((store) => store)
  const commonPoolAddress = selectedVault.value
  const commonPoolAsset = selectedVault.label

  const contract = new ethers.Contract(
    commonPoolAddress,
    CommonPool.abi,
    signer,
  )
  const [isClaiming, setIsClaiming] = useState(false)
  const { balancesData } = useTokenBalances(chainId)
  const { lastRebalance } = useCommonPoolAPR()

  const marketToken = Object.values(getV2Tokens(chainId)).find(
    (item) => item.address === commonPoolAddress,
  ) as Token

  const { data } = useSWR(
    `common-pool-redeem-${account}-${isClaiming}-${commonPoolAddress}`,
    {
      refreshInterval: 5000,
      fetcher: async () => {
        try {
          const claimableRedeem = await contract.claimableRedeem(account)
          const pendingRedeem = await contract.pendingRedeem(account)
          const decimals = await contract.decimals()

          const claimableRedeemAssets =
            await contract.claimableRedeemAssets(account)
          return {
            claimableRedeem: BigNumber.from(claimableRedeem),
            pendingRedeem: BigNumber.from(pendingRedeem),
            claimableRedeemAssets: BigNumber.from(claimableRedeemAssets),
            decimals,
          }
        } catch (e) {
          return {
            claimableRedeem: BigNumber.from(0),
            pendingRedeem: BigNumber.from(0),
            claimableRedeemAssets: BigNumber.from(0),
            symbol: '',
            decimals: 18,
            name: '',
          }
        }
      },
    },
  )

  const handleClaim = async () => {
    if (!signer || data?.claimableRedeem.isZero()) {
      return
    }
    setIsClaiming(true)
    createCommonPoolClaim(chainId, signer, {
      account,
      amount: BigNumber.from(data?.claimableRedeem.toString()),
      setPendingTxns,
      sendPaymasterTransaction: sendPaymasterTransaction,
      commonPoolAddress,
    })
      .then(() => {
        setIsClaiming(false)
      })
      .finally(() => {
        setIsClaiming(false)
      })
  }

  const pendingRedeem = getFormattedData(
    Number(data?.pendingRedeem),
    'SLV ' + commonPoolAsset,
    data?.decimals,
  )
  const claimableRedeem = getFormattedData(
    Number(data?.claimableRedeem),
    'SLV ' + commonPoolAsset,
    data?.decimals,
  )
  const claimableRedeemAssets = getFormattedData(
    Number(data?.claimableRedeemAssets),
    commonPoolAsset,
    data?.decimals,
  )
  const balance = `${
    balancesData &&
    formatTokenAmount(
      balancesData?.[marketToken?.address] || 0,
      marketToken?.decimals,
      '',
      {
        useCommas: true,
      },
    )
  } ${'SLV ' + commonPoolAsset}`

  if (below.lg) {
    return (
      <div className="border-b border-th-input-border">
        <VaultSectionHeading
          heading={`RFX ${commonPoolAsset} Shared Liquidity Vault`}
        />
        <div className="flex w-full flex-col gap-4 p-4">
          <div className="flex w-full gap-4 overflow-x-auto">
            <CardRow label={'Pending Redeem'} value={pendingRedeem} />
            <CardRow label={'Claimable Redeem'} value={claimableRedeem} />

            <CardRow
              label={'Claimable Redeem Assets'}
              value={claimableRedeemAssets}
            />

            <CardRow label={'Balance'} value={balance} />
          </div>

          <Button
            className="w-full !bg-th-active !text-th-fgd-1"
            size="small"
            onClick={handleClaim}
            disabled={data?.claimableRedeem.isZero() || isClaiming}
          >
            {isClaiming ? 'CLAIMING...' : 'CLAIM'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className={cn('flex flex-col justify-between py-3')}>
        <div className="flex flex-col gap-4">
          <CardRow
            label={'Pending Redeem'}
            value={pendingRedeem}
            className="!w-auto"
          />
          <CardRow
            label={'Claimable Redeem'}
            value={claimableRedeem}
            className="!w-auto"
          />
          <CardRow
            label={'Claimable Redeem Assets'}
            value={claimableRedeemAssets}
            className="!w-auto"
          />
          <CardRow label={'Balance'} value={balance} className="!w-auto" />
        </div>
        <Tooltip
          hasFullWidth
          showUnderline={false}
          content={
            <div>
              <div className="justify-center border-b border-th-input-border pb-4 text-base font-medium text-th-fgd-1">
                Withdrawal
              </div>
              <p className="text-border-th-fgd-3 pt-4">
                Withdrawal Queued: Your Transaction will be proccessed in
                approximately 4 hours during the next Vault Rebalancing
                <br />
                <br />
                Last Rebalance:{' '}
                {dayjs(formatTradeActionTimestampISO(lastRebalance)).format(
                  'DD MMM YYYY, hh:mm a',
                )}
              </p>
            </div>
          }
        >
          <Button
            className="my-4 !w-full !bg-th-active !text-th-fgd-1"
            size="large"
            onClick={handleClaim}
            disabled={data?.claimableRedeem.isZero() || isClaiming}
          >
            {isClaiming ? 'CLAIMING...' : 'CLAIM'}
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}

export default VaultExposure
